import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Rocket } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const BAND_STYLES = {
  "Raise-ready": "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Nearly there": "border-amber-200 bg-amber-50 text-amber-800",
  "Groundwork": "border-amber-200 bg-amber-50 text-amber-800",
};

export const ReadinessCard = () => {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!company) return;
    api.get(`/companies/${company.id}/readiness`)
      .then((r) => setData(r.data))
      .catch((e) => console.error("readiness failed", e));
  }, [company]);

  if (!data) return null;
  return (
    <Card className="rounded-xl border bg-card p-5" data-testid="readiness-card">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Rocket className="h-4 w-4 text-primary" /> Fundraise readiness
        </div>
        <Badge variant="outline" className={BAND_STYLES[data.band]} data-testid="readiness-band">{data.band}</Badge>
      </div>
      <div className="mb-2 flex items-baseline gap-1">
        <span className="h-display text-3xl font-semibold tabular-nums text-primary" data-testid="readiness-score">{data.score}</span>
        <span className="text-sm text-muted-foreground">/ 100</span>
      </div>
      <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${data.score}%` }} />
      </div>
      <div className="space-y-1.5">
        {data.items.map((it) => (
          <div key={it.key} className="flex items-center gap-2" data-testid={`readiness-item-${it.key}`}>
            {it.done ? (
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
            ) : (
              <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-slate-300" />
            )}
            <span className={`flex-1 truncate text-xs ${it.done ? "text-muted-foreground" : "text-foreground"}`} title={it.desc}>{it.label}</span>
            <span className="text-[10px] tabular-nums text-muted-foreground">{it.points} pts</span>
            {!it.done && (
              <button onClick={() => navigate(it.link)} className="text-[11px] font-medium text-primary hover:underline" data-testid={`readiness-fix-${it.key}`}>Fix</button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};
